import React, { useEffect, useId, useRef } from "react";

/**
 * Gooey morphing text — two overlaid spans crossfade with a blur, and an
 * SVG threshold filter on the wrapper hardens the blurred alpha into
 * "droplet" edges. Result: the words appear to morph/melt into each other.
 *
 * Inline-block by default with a hidden longest-word sizer so it can sit
 * within surrounding text without collapsing or jumping width.
 *
 * Note: text inside relies on the parent setting font-size / line-height
 * (we don't hardcode a size here so it can be dropped into any heading).
 */
export function GooeyText({
  texts,
  morphTime = 1,
  cooldownTime = 0.25,
  className = "",
  textClassName = "",
}) {
  const text1Ref = useRef(null);
  const text2Ref = useRef(null);
  // useId gives us a unique filter ID per instance so multiple GooeyText
  // mounts on the same page don't collide on the SVG filter reference.
  const reactId = useId();
  const filterId = `gooey-threshold-${reactId.replace(/:/g, "")}`;

  useEffect(() => {
    if (!texts || texts.length < 2) return undefined;

    let textIndex = texts.length - 1;
    let time = new Date();
    let morph = 0;
    let cooldown = cooldownTime;
    let raf;

    // Seed the two refs with the first pair so we don't flash empty text.
    if (text1Ref.current && text2Ref.current) {
      text1Ref.current.textContent = texts[textIndex % texts.length];
      text2Ref.current.textContent = texts[(textIndex + 1) % texts.length];
    }

    const setMorph = (rawFraction) => {
      if (!text1Ref.current || !text2Ref.current) return;
      text2Ref.current.style.filter = `blur(${Math.min(
        8 / rawFraction - 8,
        100,
      )}px)`;
      text2Ref.current.style.opacity = `${Math.pow(rawFraction, 0.4) * 100}%`;
      const inv = 1 - rawFraction;
      text1Ref.current.style.filter = `blur(${Math.min(8 / inv - 8, 100)}px)`;
      text1Ref.current.style.opacity = `${Math.pow(inv, 0.4) * 100}%`;
    };

    const doCooldown = () => {
      morph = 0;
      if (text1Ref.current && text2Ref.current) {
        text2Ref.current.style.filter = "";
        text2Ref.current.style.opacity = "100%";
        text1Ref.current.style.filter = "";
        text1Ref.current.style.opacity = "0%";
      }
    };

    const doMorph = () => {
      morph -= cooldown;
      cooldown = 0;
      let fraction = morph / morphTime;
      if (fraction > 1) {
        cooldown = cooldownTime;
        fraction = 1;
      }
      setMorph(fraction);
    };

    const animate = () => {
      raf = requestAnimationFrame(animate);
      const newTime = new Date();
      const shouldIncrementIndex = cooldown > 0;
      const dt = (newTime.getTime() - time.getTime()) / 1000;
      time = newTime;
      cooldown -= dt;
      if (cooldown <= 0) {
        if (shouldIncrementIndex) {
          textIndex = (textIndex + 1) % texts.length;
          if (text1Ref.current && text2Ref.current) {
            text1Ref.current.textContent = texts[textIndex % texts.length];
            text2Ref.current.textContent =
              texts[(textIndex + 1) % texts.length];
          }
        }
        doMorph();
      } else {
        doCooldown();
      }
    };

    animate();
    return () => cancelAnimationFrame(raf);
  }, [texts, morphTime, cooldownTime]);

  // Use the longest text to lock in container width so surrounding inline
  // content doesn't shift as words morph between sizes.
  const sizer = (texts || []).reduce(
    (a, b) => (a.length > b.length ? a : b),
    "",
  );

  return (
    <span className={`relative inline-block align-baseline ${className}`}>
      <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
        <defs>
          <filter id={filterId}>
            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0 0 0 255 -140"
            />
          </filter>
        </defs>
      </svg>

      {/* Invisible in-flow sizer: gives the inline-block container natural
          width + baseline so neighboring text stays aligned. Matches the
          pb on the animated spans so the container is exactly tall enough
          to show descenders without clipping. */}
      <span
        aria-hidden
        className={`invisible whitespace-nowrap pb-[0.25em] ${textClassName}`}
      >
        {sizer}
      </span>

      {/* The two animated spans live inside a wrapper that the threshold
          filter is applied to. Threshold hardens the blurred edges so each
          letter looks like a gooey droplet during the transition. */}
      <span
        className="absolute inset-0 flex items-center justify-center"
        style={{ filter: `url(#${filterId})` }}
      >
        <span
          ref={text1Ref}
          className={`absolute whitespace-nowrap pb-[0.25em] ${textClassName}`}
        />
        <span
          ref={text2Ref}
          className={`absolute whitespace-nowrap pb-[0.25em] ${textClassName}`}
        />
      </span>
    </span>
  );
}

export default GooeyText;
